import { MermaidPreview } from '../../../components/Mermaid';
import { removeHTMLTags, copyToClipboard } from '../../../utils/common';
import { Button, Typography, message, Skeleton } from 'antd';
import ReactMarkdown from 'react-markdown';
import React, { useId, useState, lazy, Suspense } from 'react';
import remarkGfm from 'remark-gfm';
import {
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
  SoundOutlined,
  CopyOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { useAIChat } from '../AiChatProvider';
import { AiProcessingSteps } from './AiProcessingSteps';

const FeedbackBox = lazy(() => import('./Feedbackbox'));

const handleCopy = (content) => {
  copyToClipboard(content);
  message.success('Copied to clipboard');
};

const defaultContent = [
  {
    type: 'markdown',
    content: '**No Response**\nUnfortunately, there is no content available at the moment. Please try again later.',
  },
];

const parseMermaidEmbeddedContent = (text) => {
  const mermaidPattern = /```mermaid([\s\S]*?)```/g;
  const blocks = [];
  let lastIndex = 0;
  text?.replace(mermaidPattern, (match, mermaidContent, offset) => {
    if (offset > lastIndex) blocks.push({ type: 'markdown', content: text?.slice(lastIndex, offset).trim() });
    blocks.push({ type: 'mermaid', content: mermaidContent.trim() });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < text?.length) blocks.push({ type: 'markdown', content: text?.slice(lastIndex).trim() });
  return blocks;
};

const RenderChatSources = React.memo(({ sources }) => {
  return (
    <ul className="space-y-4 sources-list">
      {sources.map((source, index) => (
        <li key={source.id} className="source-item">
          <div className="source-item-text">
            <a
              href={`/spaces/${source?.metadata?.workspace_id}/pages/${source?.metadata?.source_id}`}
              target="_blank"
              rel="noreferrer"
            >
              Go to reference {index + 1}
            </a>
          </div>
        </li>
      ))}
    </ul>
  );
});

const OneDocAiAnswer = React.memo(({ chat }) => {
  const { sendFeedback, currentConversation, processingSteps } = useAIChat();
  const id = useId();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [liked, setLiked] = useState(chat?.liked === true);
  const [disliked, setDisliked] = useState(chat?.liked === false);
  const [feedbackVisible, setFeedbackVisible] = useState(false);

  // Show steps when we have steps and message is pending, but not when actively streaming answer
  const showProcessingSteps = processingSteps?.length > 0 && chat.status === 'pending';

  const handleRecite = (content) => {
    if (!('speechSynthesis' in window)) {
      message.error('Speech synthesis not supported in this browser');
      return;
    }

    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      if (!content) {
        message.error('No content to recite');
        return;
      }
      const utterance = new SpeechSynthesisUtterance(content);
      utterance.rate = 1;
      utterance.lang = 'en-US';

      utterance.onend = () => {
        setIsSpeaking(false);
      };

      window.speechSynthesis.speak(utterance);
      setIsSpeaking(true);
    }
  };

  const handleLike = async () => {
    if (liked) return;

    try {
      const response = await sendFeedback({
        conversationId: currentConversation?.id,
        parentId: chat?.parent_id,
        liked: true,
      });

      setLiked(true);
    } catch (error) {
      console.error('Failed to submit like:', error);
      message.error('Failed to submit like. Please try again.');
    }
  };

  const handleDislike = async () => {
    if (disliked) return;

    try {
      await sendFeedback({
        conversationId: currentConversation?.id,
        parentId: chat?.parent_id,
        liked: false,
      });

      setDisliked(true);
      setFeedbackVisible(true);
    } catch (error) {
      console.error('Failed to submit dislike:', error);
      message.error('Failed to submit dislike. Please try again.');
    }
  };

  const handleSubmitFeedback = async ({ reason }) => {
    await sendFeedback({
      conversationId: currentConversation?.id,
      parentId: chat?.parent_id,
      liked: liked ?? false,
      reason,
    });

    setFeedbackVisible(false);
    setDisliked(true);
    setLiked(false);
  };

  const parsedResponse = chat?.content ? parseMermaidEmbeddedContent(chat?.content) : defaultContent;

  const chatContent = parsedResponse
    ?.filter((obj) => obj.type === 'markdown')
    .map((obj) => removeHTMLTags(obj.content))
    .join('\n');

  return (
    <div className="prompt-answer-box">
      <div className="prompt-answer">
        {chat.status === 'pending' && processingSteps?.length === 0 && <Skeleton active paragraph={{ rows: 2 }} />}

        {/* Processing steps container */}
        {showProcessingSteps && <AiProcessingSteps processingSteps={processingSteps} />}

        {/* Main answer content */}
        <div className="prompt-answer_layoutBottom">
          {(chat.status === 'aborted_error' || (chat.status !== 'pending' && chat?.from === 'ai')) && (
            <div className="main-ai-response" style={{ fontSize: '14px' }}>
              {parsedResponse?.map((obj, index) => {
                if (obj.type === 'markdown') {
                  return (
                    <Typography.Text key={index}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} className="ai-markdown">
                        {removeHTMLTags(obj.content)}
                      </ReactMarkdown>
                    </Typography.Text>
                  );
                } else if (obj.type === 'mermaid') {
                  return <MermaidPreview source={obj.content} id={`${id}-${index}`} />;
                }
                return null;
              })}
            </div>
          )}
          {chat.status === 'answered' && chat?.sources && <RenderChatSources sources={chat.sources} />}
        </div>

        {chat.status !== 'pending' && (
          <div
            style={{
              marginTop: '8px',
              display: 'inline-flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'nowrap',
              maxWidth: '100%',
            }}
          >
            <Button type="text" icon={<CopyOutlined />} onClick={() => handleCopy(chatContent)} />
            <Button
              type="text"
              icon={isSpeaking ? <PauseCircleOutlined /> : <SoundOutlined />}
              onClick={() => handleRecite(chatContent)}
            />

            {chat.parent_id && (
              <>
                {!disliked && (
                  <Button type="text" icon={liked ? <LikeFilled /> : <LikeOutlined />} onClick={handleLike} />
                )}
                {!liked && (
                  <Button
                    type="text"
                    icon={disliked ? <DislikeFilled /> : <DislikeOutlined />}
                    onClick={handleDislike}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      <Suspense fallback={null}>
        <FeedbackBox
          visible={feedbackVisible}
          onClose={() => setFeedbackVisible(false)}
          conversationId={currentConversation?.id}
          parentId={chat?.parent_id}
          liked={false}
          onSubmit={handleSubmitFeedback}
        />
      </Suspense>
    </div>
  );
});

export default OneDocAiAnswer;
